import { sentenceCase } from '../Shared/StringUtilities';

export enum MedicinalProductSearchBy {
    All = 'all',
    MedicinalProductPackOnly = 'medicinalProductPackOnly',
    TradeProductPackOnly = 'tradeProductPackOnly'
}

export enum MedicationValidation {
    Valid = 'valid',
    BrandPrescriptionWarning = 'brandPrescriptionWarning',
    GenericOnLemiException = 'genericOnLemiException',
    GenericOnLmbcException = 'genericOnLmbcException'
}

export enum MedicationStatus {
    Draft = 'draft',
    Finalised = 'finalised',
    Stopped = 'stopped',
    EnteredInError = 'enteredInError'
}

export enum MedicationCourse {
    ShortCourse = 'shortCourse',
    AsRequired = 'asRequired',
    Regular = 'regular',
    Daa = 'daa', // TODO used in legend, may move based on DAA implementaion
    None = 'none'
}

export enum ConsentDetailStatus {
    NotSet = 'notSet',
    Yes = 'yes',
    No = 'no'
}

export function prettyPrintMedicationCourse(course?: MedicationCourse): string {
    switch (course) {
        case MedicationCourse.ShortCourse:
            return 'Short course';
        case MedicationCourse.AsRequired:
            return 'As required (PRN)';
        case MedicationCourse.Regular:
            return 'Regular medication';
        default:
            return sentenceCase(course ?? '');
    }
}

export enum MedicationType {
    TakeHomeScript = 'takeHomeScript',
    MedicationOrder = 'medicationOrder',
    MedicationHistory = 'medicationHistory',
    EnrmcScript = 'enrmcScript',
    ComplementaryTherapyOrder = 'complementaryTherapyOrder'
}

export function prettyPrintMedicationType(type?: MedicationType): string {
    switch (type) {
        case MedicationType.TakeHomeScript:
            return 'Take home script';
        case MedicationType.MedicationOrder:
            return 'Medication order';
        case MedicationType.MedicationHistory:
            return 'Medication history';
        case MedicationType.EnrmcScript:
            return 'eNRMC script';
        case MedicationType.ComplementaryTherapyOrder:
            return 'Complementary therapy';
        default:
            return sentenceCase(type ?? '');
    }
}

export enum OutputType {
    NoOutput = 'noOutput',
    SendScript = 'sendScript',
    PrintScript = 'printScript'
}

export enum OutputStatus {
    None = 'none',
    Submitted = 'submitted',
    Accepted = 'accepted',
    Expired = 'expired',
    Printed = 'printed',
    Declined = 'declined',
    Redelivered = 'redelivered'
}

export enum DecisionSupportStatus {
    NotChecked = 'notChecked',
    Passed = 'passed',
    Information = 'information',
    Warning = 'warning',
    Alert = 'alert',
    Error = 'error'
}

export enum DecisionSupportStatusBehaviour {
    CurrentStatus = 'currentStatus',
    ExcludeNotedDecisionSupport = 'excludeNotedDecisionSupport'
}

export enum PrescriptionMonitoringStatus {
    NotChecked = 'notChecked',
    Passed = 'passed',
    NotEnabled = 'notEnabled',
    Acceptable = 'acceptable',
    Warning = 'warning',
    Alert = 'alert',
    Error = 'error'
}

export enum MedicationExpiry {
    Expired = 'expired',
    AboutToExpire = 'aboutToExpire',
    InDate = 'inDate',
    NoExpiry = 'noExpiry'
}

export enum PrescribeUsing {
    Generic = 'generic',
    Brand = 'brand'
}

export enum PaymentSchemeType {
    Private = 'private',
    Pbs = 'pbs',
    UnlistedRA = 'unlistedra'
}

export enum PbsRestrictionType {
    Unrestricted = 'unrestricted',
    Restricted = 'restricted',
    AuthorityRequired = 'authorityRequired',
    AuthorityRequiredStreamlined = 'authorityRequiredStreamlined'
}

export enum InstructionType {
    FreeTextInstruction = 'freeTextInstruction',
    DaaInstruction = 'daaInstruction',
    DosageTimeInstruction = 'dosageTimeInstruction'
}

export enum PatientMedicationAllowedAction {
    Finalise = 'finalise',
    Stop = 'stop',
    EnterInError = 'enterInError',
    Represcribe = 'represcribe',
    Delete = 'delete',
    ReviewVto = 'reviewVto',
    ReassignVto = 'reassignVto',
    Adjust = 'adjust'
}

export enum PoisonScheduleStatus {
    None = 'none',
    Warning = 'warning',
    Alert = 'alert'
}

export enum PatientMedicationHistoryStatus {
    Draft = 'draft',
    Finalised = 'finalised',
    Stopped = 'stopped',
    EnteredInError = 'enteredInError',
    Submitted = 'submitted',
    Accepted = 'accepted',
    Expired = 'expired',
    Printed = 'printed',
    Declined = 'declined',
    Redelivered = 'redelivered',
    Updated = 'updated',
    ElectronicFailure = 'electronicFailure',
    Authorised = 'authorised'
}

export enum PharmacyRequestHistoryStatus {
    Created = 'created',
    Stopped = 'stopped',
    Cancelled = 'cancelled',
    EnteredInError = 'enteredInError',
    Submitted = 'submitted',
    Accepted = 'accepted',
    Declined = 'declined',
    Sent = 'sent',
    ElectronicFailure = 'electronicFailure',
    Updated = 'updated'
}

export enum VerbalOrderStatus {
    New = 'new',
    //Accepted = 'accepted',
    Reviewed = 'reviewed',
    Rejected = 'rejected'
}

export enum CrossCheckType {
    Provider = 'provider',
    External = 'external'
}

export enum ClinicalIndicationType {
    FreeText = 'freeText',
    Coded = 'coded'
}

export enum CdsStatus {
    Available = 'available',
    Noted = 'noted'
}

export enum ProductPackGroup {
    Unrestricted = 'Unrestricted',
    Restricted = 'Restricted',
    AuthorityRequiredStreamlined = 'AuthorityRequiredStreamlined',
    AuthorityRequired = 'AuthorityRequired',
    Private = 'Private',
    S100 = 'S100'
}

export enum PbsRpbsRestrictionType {
    Unrestricted = 'Unrestricted',
    Restricted = 'Restricted',
    AuthorityRequired = 'AuthorityRequired',
    AuthorityRequiredStreamlined = 'AuthorityRequiredStreamlined'
}

export interface SupplyDetail {
    interval?: number;
    timeIntervalUnit?: string;
}

export enum PatientMedicationErxStatus {
    HealthIdentifiersDoesNotActive = 10,
    EntityIdDoesNotExists = 11
}
